import React from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { 
  Container, 
  AppBar, 
  Toolbar, 
  Typography, 
  Box,
  Paper,
  IconButton,
  Button
} from '@mui/material';
import { Home, ArrowBack } from '@mui/icons-material';
import { Link } from 'react-router-dom';

const theme = createTheme({
  palette: {
    primary: { main: '#1976d2' },
    secondary: { main: '#dc004e' },
  },
});

function ReactHookFormDocsPage() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      
      <AppBar position="static">
        <Toolbar>
          <IconButton 
            component={Link} 
            to="/" 
            color="inherit" 
            sx={{ mr: 2 }}
          >
            <Home />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            React Hook Form Documentation
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        
        <Paper sx={{ p: 4 }}>
          <Typography variant="h3" gutterBottom>
            React Hook Form 기본 학습
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 3 }}>
            React Hook Form은 성능이 뛰어나고 유연한 폼 라이브러리입니다. 
            비제어 컴포넌트를 기반으로 하여 불필요한 리렌더링을 줄이고, 
            간단한 API로 복잡한 폼 로직을 처리할 수 있습니다.
          </Typography>

          <Typography variant="h4" sx={{ mt: 4, mb: 2 }}>
            설치
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            폼 처리를 위한 핵심 패키지와 유효성 검사를 위한 Yup 스키마 라이브러리를 함께 설치합니다.
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography component="pre" sx={{ fontFamily: 'monospace' }}>
{`npm install react-hook-form @hookform/resolvers yup`}
            </Typography>
          </Paper>

          <Typography variant="h4" sx={{ mt: 4, mb: 2 }}>
            기본 사용법
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            useForm 훅은 폼의 상태를 관리하고 유효성 검사, 제출 처리 등의 기능을 제공합니다.
          </Typography>
          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
            useForm 훅 사용
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
{`import { useForm } from 'react-hook-form';

type FormData = {
  name: string;
  email: string;
};

function MyForm() {
  const { register, handleSubmit, formState: { errors } } = useForm<FormData>();
  
  const onSubmit = (data: FormData) => {
    console.log(data);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <input {...register('name')} />
      <input {...register('email')} />
      <button type="submit">Submit</button>
    </form>
  );
}`}
            </Typography>
          </Paper>

          <Typography variant="h4" sx={{ mt: 4, mb: 2 }}>
            Controller로 MUI 연동
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            MUI 컴포넌트는 비제어 컴포넌트가 아니므로 Controller를 사용해서 React Hook Form과 연결해야 합니다.
          </Typography>
          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
            기본 TextField 연동
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
{`import { Controller } from 'react-hook-form';
import { TextField } from '@mui/material';

<Controller
  name="productName"
  control={control}
  render={({ field }) => (
    <TextField
      {...field}
      label="Product Name"
      variant="outlined"
      error={!!errors.productName}
      helperText={errors.productName?.message}
    />
  )}
/>`}
            </Typography>
          </Paper>

          <Typography variant="h4" sx={{ mt: 4, mb: 2 }}>
            유효성 검사 (Yup)
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Yup은 객체 스키마 유효성 검사 라이브러리입니다. 복잡한 유효성 검사 규칙을 선언적으로 정의할 수 있습니다.
          </Typography>
          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
            스키마 정의
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
{`import * as yup from 'yup';

const schema = yup.object({
  name: yup.string()
    .required('이름은 필수입니다')
    .min(2, '최소 2글자 이상'),
  
  age: yup.number()
    .required('나이는 필수입니다')
    .min(0, '0 이상이어야 합니다')
    .max(150, '150 이하여야 합니다'),
    
  email: yup.string()
    .email('올바른 이메일 형식이 아닙니다')
    .required('이메일은 필수입니다')
});`}
            </Typography>
          </Paper>

          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
            스키마 적용
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
{`import { yupResolver } from '@hookform/resolvers/yup';

const { control, handleSubmit, formState: { errors } } = useForm({
  resolver: yupResolver(schema),
  defaultValues: {
    name: '',
    age: 0,
    email: ''
  }
});`}
            </Typography>
          </Paper>

          <Typography variant="h4" sx={{ mt: 4, mb: 2 }}>
            주요 기능들
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            React Hook Form이 제공하는 유용한 기능들을 살펴보겠습니다.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
            1. watch - 실시간 값 감시
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
{`const watchedValue = watch('productName');
const allValues = watch(); // 모든 값

// 특정 필드들만 감시
const watchedFields = watch(['name', 'price']);`}
            </Typography>
          </Paper>

          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
            2. reset - 폼 초기화
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
{`const { reset } = useForm();

// 기본값으로 초기화
reset();

// 특정 값으로 초기화
reset({
  name: 'New Product',
  price: 100
});`}
            </Typography>
          </Paper>

          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
            3. 폼 상태 관리
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
{`const { 
  formState: { 
    errors,        // 에러 정보
    isSubmitting,  // 제출 중인지
    isDirty,       // 값이 변경되었는지
    isValid        // 유효한지
  } 
} = useForm();

<Button 
  type="submit" 
  disabled={isSubmitting || !isDirty || !isValid}
>
  {isSubmitting ? 'Saving...' : 'Save'}
</Button>`}
            </Typography>
          </Paper>

          <Typography variant="h4" sx={{ mt: 4, mb: 2 }}>
            실제 사용 예시
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
{`import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

const productSchema = yup.object({
  name: yup.string().required('상품명은 필수입니다'),
  price: yup.number().required('가격은 필수입니다').min(0),
  category: yup.string().required('카테고리를 선택해주세요')
});

function ProductForm() {
  const { control, handleSubmit, formState: { errors } } = useForm({
    resolver: yupResolver(productSchema)
  });

  const onSubmit = (data) => {
    console.log('제출된 데이터:', data);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Controller
        name="name"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label="상품명"
            error={!!errors.name}
            helperText={errors.name?.message}
          />
        )}
      />
      
      <Button type="submit">등록</Button>
    </form>
  );
}`}
            </Typography>
          </Paper>

        </Paper>

        <Box sx={{ mt: 3 }}>
          <Button 
            component={Link}
            to="/"
            variant="outlined"
            startIcon={<ArrowBack />}
          >
            Back to Home
          </Button>
        </Box>

      </Container>
    </ThemeProvider>
  );
}

export default ReactHookFormDocsPage;
